import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import logo from '../../static/images/logo.png';
import Button from '../../components/Button/Button';
import FullScreen from '../../components/FullScreen/FullScreen';
import Loading from 'components/Loading/Loading';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import '../../theme/Forms.scss';
import colors from '../../theme/Colors.module.scss';

import {
  Button as RegularButton,
  Flex,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import { setUser, setToken } from 'modules/app/appSlice';
import { useLoginMutation } from 'modules/app/appApi';

const defaults = { userEmail: '', userPassword: '' };
const schema = Yup.object().shape({
  userEmail: Yup.string()
    .email('Invalid email.')
    .required('Email is required.')
    .trim(),
  userPassword: Yup.string().required('Password is required.').trim(),
});

const Login = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Selectors
  const token = useSelector(state => state.app.token);

  const [loginError, setLoginError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [login, { isLoading: isAuthenticating }] = useLoginMutation();

  // Redirect if already logged in
  useEffect(() => {
    if (token) {
      const redirectTo = location.state?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, location, token]);

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);
    const { userEmail, userPassword } = values;
    const userInfo = {
      user: {
        email: userEmail,
        password: userPassword,
      },
    };

    try {
      const loginUser = await login(userInfo).unwrap();

      dispatch(setUser(loginUser.user));
      dispatch(setToken(loginUser.token));

      // Redirect after login
      // const redirectTo = location.state?.from || '/dashboard';
      // navigate(redirectTo, { replace: true });

      // Always navigate to dashboard after login
      navigate('/dashboard', { replace: true });

      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Log in successfully',
        })
      );
    } catch (error) {
      setLoginError(true);
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to log in.',
        })
      );
    } finally {
      form.setSubmitting(false);
    }
  };

  const MaybeLoginError = ({ loginError }) => {
    return loginError ? (
      <div className="input-feedback">
        Invalid username or password. Please try again, or click "Forgot your
        password?" to initiate account recovery.
      </div>
    ) : (
      <span />
    );
  };

  return (
    <FullScreen className="authFormContainer">
      <Formik
        initialValues={defaults}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {form => {
          const { isSubmitting } = form;

          return (
            <Form className="authForm">
              <img className="authLogo" src={logo} alt="" />

              <Flex
                flexDirection="column"
                marginTop="1rem"
                marginBottom="2rem"
                gap="2rem"
              >
                <MaybeLoginError loginError={loginError} />

                <Field name="userEmail">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.userEmail && form.touched.userEmail
                      }
                    >
                      <Input
                        {...field}
                        placeholder="Email*"
                        focusBorderColor={colors.primary600}
                        backgroundColor={colors.white}
                        disabled={isSubmitting}
                      />
                      <FormErrorMessage>
                        {form.errors.userEmail}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Field name="userPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.userPassword && form.touched.userPassword
                      }
                    >
                      <InputGroup size="md">
                        <Input
                          {...field}
                          placeholder="Password*"
                          focusBorderColor={colors.primary600}
                          backgroundColor={colors.white}
                          disabled={isSubmitting}
                          type={showPassword ? 'text' : 'password'}
                        />
                        <InputRightElement width="4.5rem">
                          <RegularButton
                            h="1.75rem"
                            size="sm"
                            onClick={() => {
                              setShowPassword(!showPassword);
                            }}
                          >
                            {showPassword ? (
                              <i className="fas fa-eye-slash" />
                            ) : (
                              <i className="fas fa-eye" />
                            )}
                          </RegularButton>
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.userPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </Flex>

              <Button
                type="submit"
                text={
                  isAuthenticating ? (
                    <Loading size="sm" thickness="2px" color="white" />
                  ) : (
                    'Log in'
                  )
                }
                textColor={colors.white}
                btnStyle="solid"
                hoverColor={colors.primary500}
                textHoverColor={colors.white}
                disabled={form.isSubmitting}
              />

              <Text as="p" className="loginOptions">
                <Link to="/reset-password">Forgot your password?</Link>.
              </Text>
            </Form>
          );
        }}
      </Formik>
    </FullScreen>
  );
};

export default Login;
